import c from 'classnames';
import { PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';
import styles from './Expander.module.css';

type StateDispatcherType = React.Dispatch<React.SetStateAction<boolean>>;

type Props = {
  trigger: (
    isExpanded: boolean,
    setIsExpanded: StateDispatcherType,
  ) => React.ReactNode;
  containerClassName?: string;
  contentContainerClassName?: string;
  collapsedHeight?: number;
  hideFade?: boolean;
};

export function Expander({
  trigger,
  containerClassName,
  contentContainerClassName,
  collapsedHeight = 150,
  hideFade,
  children,
}: PropsWithChildren<Props>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(collapsedHeight);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!innerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContentHeight(entry.contentRect.height);
    });
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);

  const needsExpansion = contentHeight > collapsedHeight;

  return (
    <div className={c(containerClassName, styles.container)}>
      <div
        className={c(contentContainerClassName, styles.contentContainer, {
          [styles.fade]: needsExpansion && !isExpanded && !hideFade,
        })}
        style={{
          height: isExpanded || !needsExpansion ? contentHeight : collapsedHeight,
        }}
      >
        <div ref={innerRef} className={styles.inner}>
          {children}
        </div>
      </div>
      {needsExpansion && trigger(isExpanded, setIsExpanded)}
    </div>
  );
}
